import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  isEmpty,
  values,
} from "ramda";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as Elipse } from "../../../../assets/svg/elipse.svg";
import { ReactComponent as NotificationSvg } from "../../../../assets/svg/notification.svg";
import { tripPointsReal } from "../../../../shared/db/report-db";
import {
  countDateWithUserAndTimezoneOffset,
  isMobile,
  userCurrentOffset,
  utcOffset,
} from "../../../../shared/helpers";
import {
  iFullStoreState,
  iTrip,
  Units,
} from "../../../../shared/interfaces";
import { useRedux } from "../../../../states/redux-state";
import { DashboardSize } from "../../../../stores/reducers/dashboardInfo";
import { fullCloseDashboard } from "../../../../stores/reducers/dashboardInfo/AC";
import { IPointsContainer } from "../../../../stores/reducers/devicesTripsPoints";
import {
  hideReportTrips,
  setReportTrip,
  toggleShowReportTrip,
} from "../../../../stores/reducers/devicesTripsPoints/AC";
import { Actions as GeneralActions, useLocalStorage } from "../../../../stores/reducers/general-reducers";
import { Button, ButtonSize, ButtonStyles } from "../../../Button";
import { Row } from "../../../elements/flex";
import { iHiddenReportData } from "../vis-report-data";
import { ActivityDeetsCol } from "./cells/activity-deets";
import { TripAndTravelCell } from "./cells/TripAndTravelCell";
import "./table-wrapper.scss";

type IReportTripRowProps = {
  deviceId: string;
  timezone: string;
  units: Units;
  current: iTrip;
  showAllRoute: boolean;
  device?: any;
  showTrip: (item: { device: string; tripId: string }, tripPoints?: any, zoomToTrip?: boolean) => void;
};

export const ReportTripRow = ({
  current: row,
  deviceId,
  units,
  timezone,
  showTrip,
  device
}: IReportTripRowProps) => {
  const dispatch = useDispatch();

  const dashboardInfo = useRedux((s) => s.dashboardInfo);

  const tripPoints = useSelector<iFullStoreState, IPointsContainer | undefined>(
    (s) => s.devicesTripsPoints.getIn([deviceId, "tripsPoints", row.tripId]) as IPointsContainer | undefined,
    (l, r) => l?.keySeq?.()?.last?.() === r?.keySeq?.()?.last?.()
  );

  const [showAlerts, setShowAlerts] = React.useState(false);
  const showRoute = useSelector<iFullStoreState, boolean>(
    (s) =>
      (s.devicesTripsPoints.getIn([deviceId, "reportTripsIds", row.tripId]) as boolean) ||
      false
  );

  const showRouteToggle = () => {
    if (!showRoute) {
      // First, hide ALL trips from ALL devices (only one trip can be active at a time)
      dispatch(hideReportTrips());

      // Then show the new trip
      if (isMobile) {
        dispatch(GeneralActions.SHOW_SEARCH);
        dispatch(fullCloseDashboard());
      }
      showTrip({ device: deviceId, tripId: row.tripId }, null, true);
      tripPointsReal(row.tripId)
          .then(values)
          .then((points: any) => {
            dispatch(setReportTrip(deviceId, row.tripId, points));
          });
    } else {
      // Hide the current trip
      dispatch(toggleShowReportTrip(deviceId, row.tripId, false));
    }
  };

  const empty = values(row.alertActivity).filter((a) => a > 0);
  const emptyAlerts = isEmpty(empty);

  // moment().isDST() don't using, cause userCurrent Offset already contain or not DST
  // that's why we pass instead moment().isDST() -> just false, to utcOffset
  const deviceOffset = utcOffset(timezone, false);
  const diff = userCurrentOffset - deviceOffset;

  const start = countDateWithUserAndTimezoneOffset(row.startDate, timezone);
  const end = countDateWithUserAndTimezoneOffset(row.endDate, timezone);
  const sameDay = start.dayOfYear() == end.dayOfYear();

  const [hiddenFields] = useLocalStorage<iHiddenReportData>(`hidden-report-data:travel`, {});

  return (
    <>
      <tr key={`${row.tripId}-1`}>
        <td colSpan={2} style={{ backgroundColor: "#f0f0f0f0", overflow: 'hidden' }}>
          <Row style={{ flexDirection: "row" }}>
            <div className={"trip-date-wrap"}>
              <div className={`text-wrapper ${
                dashboardInfo.size === DashboardSize.OPEN ? "open" : ""
              }`}>
                <p style={{ whiteSpace: "nowrap", flexGrow: 1 }}>
                  <Elipse className="start-date-icon" />{" "}
                  {start.format("MM/DD/YYYY")} {start.format("h:mmA")}
                </p>
                <p className="startAdress">{row.startAddress}</p>
              </div>
              <div
                className={`actionsSection ${isMobile || window["cordova"] ? "d-flex" : ""} ${
                  dashboardInfo.size === DashboardSize.OPEN ? "open" : ""
                }`}
              >
                {showRoute && !tripPoints ? (
                  <FontAwesomeIcon icon="spinner" spin />
                ) : (
                  <button
                    className={`showTrip ${showRoute ? 'active' : ''}`}
                    onClick={() => showRouteToggle()}
                  >
                    {showRoute ? 'Hide Trip' : 'Show Trip'}
                  </button>
                )}
                <Button
                  styleType={
                    emptyAlerts
                      ? ButtonStyles.GRAY_INACTIVE
                      : ButtonStyles.YELLOW_WHITE
                  }
                  style={
                    isMobile || window["cordova"]
                      ? { height: "max-content" }
                      : {}
                  }
                  size={ButtonSize.XSM}
                  disabled={emptyAlerts}
                  onClick={() =>
                    setShowAlerts(!showRoute ? showAlerts : !showAlerts)
                  }
                >
                    <NotificationSvg
                      className={
                        isMobile || window["cordova"] ? "icon-smd" : "icon-sm"
                      }
                    />
                </Button>
            </div>
            </div>
          </Row>
        </td>
      </tr>
      <tr key={`${row.tripId}-3`}>
        <td colSpan={2}>
          {!showAlerts ? (
            <Row
              disableStyles={true}
              className={`trip-wraper ${
                dashboardInfo.size === DashboardSize.OPEN ? "open" : ""
              }`}
              justify="space-evenly"
              style={{ fontSize: 11.5 }}
            >
              <div>
                <TripAndTravelCell
                  row={row}
                  hiddenFields={hiddenFields}
                  device={device}
                />
              </div>
            </Row>
          ) : (
            <ActivityDeetsCol
              tripId={row.tripId}
              units={units}
              deviceId={deviceId}
              errorCloser={() => setShowAlerts(false)}
              timezone={timezone}
            />
          )}
        </td>
      </tr>
      <tr key={`${row.tripId}-4`}>
        <td
          className="footer"
          colSpan={2}
          style={{
            backgroundColor: "#f0f0f0f0",
            fontSize: isMobile ? 9 : "inherit",
          }}
        >
          <Row
            justify="flex-start"
            style={{ fontSize: isMobile ? 9 : "inherit" }}
          >
            <div className="trip-date-wrap">
              <span className="endDate">
                <Elipse className="end-date-icon" /> {end.format("h:mmA")}
              </span>
              <span className="address">{row.endAddress}</span>
            </div>
          </Row>
        </td>
      </tr>
    </>
  );
};
